import React, { useEffect, useState } from 'react';
import { ApiClient } from '../../api/client';
import { KeyRound, Copy, Check, RefreshCw, X, AlertTriangle, ExternalLink } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
// TenantLoginLinkModal – a szervezet egyedi belépési linkje
//
// A link (/login/<token>) véletlen azonosítót tartalmaz, a szervezet
// nevéből vagy slugjából nem következtethető ki. Ezen az oldalon csak a
// szervezet saját, „belépési oldalon megjelenik” jelölésű felhasználói
// látszanak, és csak a szervezet felhasználói léphetnek be.
// Új link generálásakor a régi azonnal megszűnik.
// ═══════════════════════════════════════════════════════════════

interface Props {
  tenantId: number;
  tenantName: string;
  onClose: () => void;
}

export const TenantLoginLinkModal: React.FC<Props> = ({ tenantId, tenantName, onClose }) => {
  const [url, setUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);

  const toUrl = (p: string) => `${window.location.origin}${p}`;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    ApiClient.getTenantLoginLink(tenantId).then((res) => {
      if (!alive) return;
      if (res.ok && res.data?.path) setUrl(toUrl(res.data.path));
      else setError(res.error || 'A belépési link nem kérhető le');
      setLoading(false);
    });
    return () => { alive = false; };
  }, [tenantId]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setError('A vágólapra másolás nem sikerült – jelölje ki és másolja kézzel.');
    }
  };

  const regenerate = async () => {
    setBusy(true);
    setError(null);
    const res = await ApiClient.regenerateTenantLoginLink(tenantId);
    if (res.ok && res.data?.path) {
      setUrl(toUrl(res.data.path));
      setConfirmRegen(false);
    } else {
      setError(res.error || 'Az új link létrehozása nem sikerült');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-2xl space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
              <KeyRound className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Egyedi belépési link</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">{tenantName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer" title="Bezárás">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
          Ezen a címen a szervezet munkatársai a saját belépési oldalukat érik el. Az oldalon csak a szervezet
          azon felhasználói jelennek meg, akiknél a Felhasználók menüben a <b>„Megjelenítés a belépési oldalon”</b> be van kapcsolva, és csak
          a szervezet felhasználói léphetnek be. A link nem találgatható ki, ezért más szervezetek nem ismerik.
        </p>

        {loading ? (
          <div className="p-4 text-center text-sm text-slate-500">Betöltés…</div>
        ) : url ? (
          <div className="flex items-stretch gap-2">
            <input
              readOnly
              value={url}
              onFocus={(e) => e.currentTarget.select()}
              className="flex-1 min-w-0 px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-mono text-slate-800 dark:text-slate-200"
            />
            <button
              onClick={copy}
              className="px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold flex items-center gap-1.5 cursor-pointer"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copied ? 'Másolva' : 'Másolás'}</span>
            </button>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-2.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center"
              title="Megnyitás új lapon"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        ) : null}

        {error && (
          <div className="p-3 rounded-xl text-xs bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300">
            {error}
          </div>
        )}

        <div className="pt-3 border-t border-slate-200 dark:border-slate-800">
          {!confirmRegen ? (
            <button
              onClick={() => setConfirmRegen(true)}
              disabled={loading || busy}
              className="text-xs font-semibold text-amber-700 dark:text-amber-400 hover:underline flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Új link létrehozása (ha a régi illetéktelen kézbe került)
            </button>
          ) : (
            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 space-y-2">
              <div className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-300">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Az új link létrehozása után a jelenlegi link azonnal megszűnik. A munkatársaknak az új címet kell használniuk. A link azonosítója az Archívum letöltött ZIP-csomagjainak jelszava is: a korábban letöltött csomagok jelszava a régi azonosító marad – jegyezze fel a csomag mellé.</span>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirmRegen(false)} className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-medium cursor-pointer">Mégse</button>
                <button
                  onClick={regenerate}
                  disabled={busy}
                  className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {busy && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  Új link létrehozása
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantLoginLinkModal;
